/**
 * Donut — rating distribution donut chart (IA §2.2.3).
 *
 * Recharts `PieChart` with inner + outer radii producing a donut. The
 * center label (e.g. "4.3 avg (N rated)") is absolutely positioned inside
 * the chart wrapper since Recharts exposes no first-class API for it.
 *
 * The palette applies in segment order when the caller doesn't pin a
 * colour on the data point. Desktop renders the legend to the right of
 * the chart; mobile stacks it below (Tailwind `md:flex-row` vs `flex-col`).
 *
 * Bug G3 fix:
 *   - Chart was rendering ~220px while the card slot allocated ~320-400px,
 *     leaving huge dead padding. Switch to a `ResponsiveContainer` inside a
 *     flex-1 sizing box so the donut fills the available card width.
 *   - Segment click is now interactive: the caller receives the clicked
 *     segment via `onSegmentClick`, and the selected slice gets a slightly
 *     wider outer radius + % share rendered next to its legend entry.
 */

import * as React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import { useIsTouchOnly } from '@/components/charts/ChartTooltip';
import { chartPalette } from '@/lib/chart-palette';

export interface DonutSegment {
  label: string;
  value: number;
  /** Optional pinned colour; falls back to DEFAULT_PALETTE by index. */
  color?: string;
}

export interface DonutProps {
  data: DonutSegment[];
  /**
   * Centred inside the donut hole. Accepts ReactNode so callers can render
   * multi-line content (e.g. a big "4.3 avg" with a small "(N rated)" line
   * below) — single-line strings overflow the hole at narrow column widths
   * because the inner radius is ~50% of the chart box and the label was
   * sized for the wider 1440px+ layout.
   */
  centerLabel?: React.ReactNode;
  /**
   * Pixel height of the chart box. Default 280 — up from the old 220 so the
   * chart stops floating inside the card plashka. Width is ResponsiveContainer
   * driven; the container box clamps height only.
   */
  size?: number;
  /**
   * Fired when the user clicks a segment. Caller decides what to do with it
   * (highlight → filter, inspect, drill-down). `null` means the user clicked
   * the already-selected segment so selection toggles off.
   */
  onSegmentClick?: (segment: DonutSegment | null) => void;
  /** Controlled selection — label of the currently highlighted segment. */
  selectedLabel?: string | null;
  /** Nothing is counted yet. Same PROP as RatingDistribution /
   *  TimeSavedChart / LatencyDistribution, so no caller hand-rolls one, and
   *  distinct from an empty result: "not counted yet" is not "no ratings in
   *  period". Unlike those three, this chart has no fixed height to reserve —
   *  its box is `aspect-square` off the column width, with the legend beside
   *  it (>=md) or under it (<md). The skeleton mirrors the chart column
   *  exactly; the legend column is NOT reserved, so below `md`, where the
   *  legend stacks, the card still grows by the legend's height when the
   *  data lands. `legendRows` buys that back for a caller that knows how many
   *  rows to expect. */
  loading?: boolean;
  /** How many legend rows to reserve while `loading`. Callers that know the
   *  series count up front (a fixed set of ratings, of complexity classes)
   *  pass it; 0 reserves nothing. */
  legendRows?: number;
}

// Palette spans the 1★-5★ rating range on Overview; applied in index order
// so 1★ is red and 5★ is green without forcing the caller to set `color`.
// Pulled from the chart-palette SSOT so this donut and the Quality bar
// histogram (RatingDistribution.tsx) can never disagree on what "2★ colour"
// looks like.
const DEFAULT_PALETTE = [
  chartPalette.rating[1],
  chartPalette.rating[2],
  chartPalette.rating[3],
  chartPalette.rating[4],
  chartPalette.rating[5],
];

export function Donut({
  data,
  centerLabel,
  size = 280,
  onSegmentClick,
  selectedLabel,
  loading = false,
  legendRows = 0,
}: DonutProps): React.ReactElement {
  const hasData = data && data.length > 0 && data.some((d) => d.value > 0);
  const total = React.useMemo(
    () => data.reduce((acc, d) => acc + d.value, 0),
    [data],
  );
  const isInteractive = typeof onSegmentClick === 'function';
  // Hover-highlight parity with bar charts (RatingDistribution /
  // LatencyDistribution / TimeSavedChart): clickable slices paint a
  // foreground stroke on mouse-over, and on touch devices every clickable
  // slice carries a permanent low-opacity outline since hover never fires.
  // Same visual vocabulary as `activeBar={{ stroke: foreground, 1.5 }}` and
  // the touch-permanent 1px/0.6 outline the bar charts ship.
  const isTouchOnly = useIsTouchOnly();
  const [hoveredIdx, setHoveredIdx] = React.useState<number | null>(null);

  if (loading) {
    // Same wrapper and the same chart box classes as the loaded tree below.
    // Not pixel-exact: the chart box is `flex-1` of whatever the legend leaves,
    // and a skeleton legend cannot know how wide the real labels will be, so a
    // card can still settle by a few tens of pixels when the data lands.
    return (
      <div
        data-testid="donut-skeleton"
        className="flex flex-col md:flex-row items-center gap-4 w-full"
      >
        <div className="relative flex-1 w-full min-w-0 max-w-[360px] aspect-square rounded-full bg-muted animate-pulse" />
        {legendRows > 0 && (
          <ul className="flex flex-col gap-1 text-sm">
            {Array.from({ length: legendRows }).map((_, i) => (
              <li key={i} className="h-5 w-28 rounded bg-muted animate-pulse" />
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (!hasData) {
    return (
      <div
        data-testid="donut-empty"
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height: size }}
      >
        No ratings in period
      </div>
    );
  }

  // Radii. Recharts `Cell` doesn't honour a per-slice `outerRadius`, so
  // selection emphasis is done with an extra coloured stroke instead of
  // popping the slice — the stroke reads as a "selected" border without
  // fighting recharts' geometry.
  //
  // Clipping fix: Recharts places the pie centre at (cx=50%, cy=50%) of its
  // ResponsiveContainer. When we passed pixel radii derived from `size`
  // (chart height), on narrow viewports the container was near-square
  // (~220x280 on iPhone SE) but the radius was computed from height alone —
  // so an outerRadius of 115px in a 220px-wide container pushed the ring,
  // plus the 3px selection stroke, past the right edge of the SVG. The fix
  // is to pass percentage radii, which Recharts resolves against the
  // container's *minimum* dimension internally. 85 % outer + 55 % inner
  // always leaves a 15 % breathing margin on every side for the selection
  // stroke and paddingAngle gap, regardless of container aspect ratio.
  const outerR = '78%';
  const innerR = '50%';

  const handleClick = (seg: DonutSegment): void => {
    if (!onSegmentClick) return;
    // Clicking the already-selected segment clears selection (toggle).
    if (selectedLabel === seg.label) {
      onSegmentClick(null);
    } else {
      onSegmentClick(seg);
    }
  };

  return (
    <div
      data-testid="donut"
      className="flex flex-col md:flex-row items-center gap-4 w-full"
    >
      <div
        // `[&_*:focus]:outline-none` suppresses the browser's default SVG
        // focus outline on clicked pie sectors (bug B9). Recharts `Sector`
        // elements receive keyboard focus on click and would otherwise paint
        // a dashed bounding rectangle around the slice, fighting the
        // intended arc-stroke selection affordance below.
        // `flex-1 w-full` lets the chart claim the whole card width on mobile
        // and share the row with the legend on >=md. `min-w-0` lets flex
        // children shrink below their intrinsic size (required so narrow
        // viewports don't force a 220px minimum that overflows the card).
        // `aspect-square` caps the SVG box to width=height, keeping the
        // donut centered and preventing a tall/narrow container from
        // pushing the ring past the horizontal viewBox bounds.
        className="relative flex-1 w-full min-w-0 max-w-[360px] aspect-square [&_*:focus]:outline-none [&_*]:focus:outline-none"
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="label"
              innerRadius={innerR}
              outerRadius={outerR}
              paddingAngle={1}
              isAnimationActive={false}
            >
              {data.map((seg, i) => {
                const isSelected = selectedLabel === seg.label;
                const isHovered = isInteractive && hoveredIdx === i;
                const fill =
                  seg.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];
                // When a segment is selected, dim unselected slices so the
                // focused slice visually pops without recharts geometry
                // tricks (per-cell outerRadius isn't supported).
                const shouldDim =
                  selectedLabel !== undefined &&
                  selectedLabel !== null &&
                  !isSelected;
                // Stroke priority (highest first): selected > hovered >
                // touch-permanent. Matches the bar-chart vocabulary so the
                // two chart families read as the same design language.
                let stroke: string = 'transparent';
                let strokeWidth = 0;
                let strokeOpacity = 1;
                if (isSelected) {
                  stroke = 'hsl(var(--foreground))';
                  strokeWidth = 3;
                } else if (isHovered) {
                  stroke = 'hsl(var(--foreground))';
                  strokeWidth = 1.5;
                } else if (isInteractive && isTouchOnly && !shouldDim) {
                  stroke = 'hsl(var(--foreground))';
                  strokeWidth = 1;
                  strokeOpacity = 0.6;
                }
                return (
                  <Cell
                    key={seg.label}
                    fill={fill}
                    fillOpacity={shouldDim ? 0.35 : 1}
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                    strokeOpacity={strokeOpacity}
                    onClick={
                      isInteractive ? () => handleClick(seg) : undefined
                    }
                    onMouseEnter={
                      isInteractive ? () => setHoveredIdx(i) : undefined
                    }
                    onMouseLeave={
                      isInteractive ? () => setHoveredIdx(null) : undefined
                    }
                    cursor={isInteractive ? 'pointer' : 'default'}
                    // Suppress the browser's default SVG focus outline —
                    // it renders as a dashed bounding rectangle around the
                    // clicked slice and fights the intended arc-stroke
                    // selection affordance (bug B9).
                    style={{ outline: 'none' }}
                    tabIndex={-1}
                    data-testid={`donut-cell-${seg.label}`}
                  />
                );
              })}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {centerLabel && (
          <div
            data-testid="donut-center-label"
            className="absolute inset-0 flex items-center justify-center pointer-events-none text-sm font-medium text-center px-4"
          >
            {centerLabel}
          </div>
        )}
      </div>
      <ul
        data-testid="donut-legend"
        className="flex flex-col gap-1 text-sm"
      >
        {data.map((seg, i) => {
          const fill =
            seg.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length];
          const share = total > 0 ? (seg.value / total) * 100 : 0;
          const isSelected = selectedLabel === seg.label;
          return (
            <li
              key={seg.label}
              className={`flex items-center gap-2 rounded px-1 py-0.5 ${
                isInteractive ? 'cursor-pointer hover:bg-muted/50' : ''
              } ${isSelected ? 'bg-muted' : ''}`}
              onClick={isInteractive ? () => handleClick(seg) : undefined}
              onMouseEnter={
                isInteractive ? () => setHoveredIdx(i) : undefined
              }
              onMouseLeave={
                isInteractive ? () => setHoveredIdx(null) : undefined
              }
              data-testid={`donut-legend-${seg.label}`}
              data-selected={isSelected ? 'true' : 'false'}
            >
              <span
                className="inline-block h-3 w-3 rounded-sm"
                style={{ background: fill }}
              />
              <span className="text-muted-foreground">{seg.label}</span>
              <span className="font-medium tabular-nums">{seg.value}</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                ({share.toFixed(1)}%)
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
